import { type FormikProps } from "formik";
import { Component } from "react";
import { type DropdownItemProps } from "semantic-ui-react";
import { ValidatedForm } from "../../../../../components";
import { type PackFormProps } from "../types";
import { type PackFormState, type PackView } from "../../types/state";
import EndpointsToGetSasLink from "../../../../../enums/endpointsToGetSasLink";
import TrialPeriodSettings from "../TrialPeriodSettings/TrialPeriodSettings";
import { FeatureFlags } from "featureFlags";
import "./packForm.scss";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { type LDProps } from "features/LDProps";
import ImagePreview from "components/media/imagePreview/ImagePreview";
import {
  IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS,
  IMAGE_PREVIEW_PLACEHOLDER_VARIANTS,
} from "components/media/imagePreview/placeholders";

export type PackFormPropsAll = PackFormProps & FormikProps<PackView> & LDProps;

export class PackForm extends Component<PackFormPropsAll, PackFormState> {
  constructor(props: PackFormPropsAll) {
    super(props);
    const { values } = this.props;
    this.state = { selectedItemsIds: values.packFilters };
  }

  isPaidPackEnabled = (): boolean => {
    return this.props.flags?.[FeatureFlags.PaidPacks] ?? false;
  };

  render() {
    const { dirty, isLoading, disablePreventTransitionPrompt } = this.props;
    return (
      <ValidatedForm
        dirty={dirty}
        unsavedChangesPrompt={{
          title: "Exit Without Saving?",
          message: "Are you sure you want to exit without saving this pack? All information entered will be lost.",
        }}
        isLoading={isLoading}
        isSubmitting={isLoading}
        formWidthComputer={9}
        disablePreventTransitionPrompt={disablePreventTransitionPrompt}
      >
        {this.renderFields()}
      </ValidatedForm>
    );
  }

  renderFields = (): any => {
    const { values, disabled, onBlur, onIsValidChange, packFilters, packTypes, packTrialPeriods, save, isPrivatePack } =
      this.props;
    const validatedFieldProps = this.getValidateFieldProps();

    const dropdownOptions: DropdownItemProps[] = packTypes
      .filter((option) => option.name !== "Private")
      .map((option) => {
        return {
          text: option.name,
          value: option.id,
        };
      });

    return (
      <div className="pack-form-container">
        {!isPrivatePack && <h4 className="header">MARKETPLACE LISTING INFORMATION</h4>}
        <ValidatedForm.InputField
          label="Title"
          value={values.title}
          propertyName="title"
          markAsRequired
          onIsFormValidChange={onIsValidChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="Enter Pack Title"
          {...validatedFieldProps}
        />
        <ValidatedForm.InputField
          label="Short Description"
          value={values.description}
          propertyName="description"
          markAsRequired
          onIsFormValidChange={onIsValidChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="Enter Short Description"
          {...validatedFieldProps}
        />
        {!isPrivatePack && (
          <>
            <ValidatedForm.TextEditorField
              label="Long Description"
              value={values.longDescription}
              propertyName="longDescription"
              markAsRequired
              onIsFormValidChange={onIsValidChange}
              onBlur={onBlur}
              disabled={disabled}
              placeholder="Enter Long Description"
              {...validatedFieldProps}
            />
            <h4 className="header">PACK IMAGES</h4>
            <p>
              Upload up to 5 images. They will be used to showcase this pack in the marketplace. At least one image is
              required.
            </p>
          </>
        )}
        <ImagePreview
          label="Main Image"
          value={values.thumbnailImageUrl}
          propertyName="thumbnailImageUrl"
          endpointToGetSasLink={EndpointsToGetSasLink.Pack}
          className="field custom-image-preview"
          {...validatedFieldProps}
          markAsRequired
          onIsFormValidChange={(isValid: boolean) => isValid && onBlur("thumbnailImageUrl")}
          disabled={disabled}
          editBtnEnabled={false}
          combinedIcon
          placeholder={IMAGE_PREVIEW_PLACEHOLDER_VARIANTS.image}
          headerPlaceholder={IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS.image}
        />
        {!isPrivatePack && (
          <>
            <p>Supporting Images</p>
            <div className="supporting-images">
              {values.mediaItems.map((mi, index) => (
                <ImagePreview
                  key={index}
                  value={mi.url}
                  propertyName={`mediaItems[${index}].url`}
                  endpointToGetSasLink={EndpointsToGetSasLink.Pack}
                  className="field custom-image-preview"
                  onImageChanged={(_: string) => onBlur("mediaItems")}
                  {...validatedFieldProps}
                  disabled={disabled}
                  editBtnEnabled={false}
                  removeBtnEnabled
                  combinedIcon
                  placeholder={IMAGE_PREVIEW_PLACEHOLDER_VARIANTS.image}
                  headerPlaceholder={IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS.image}
                />
              ))}
            </div>
            <h4 className="header">PACK SETTINGS</h4>
            <ValidatedForm.DropdownField
              {...validatedFieldProps}
              label="Pack Type"
              value={values.typeId}
              propertyName="typeId"
              options={dropdownOptions}
              placeholder="Select Pack Type"
              onIsFormValidChange={onIsValidChange}
              disabled={disabled}
              onBlur={onBlur}
              onChangeCallback={save}
              clearable
            />
            {Object.entries(packFilters).map(([key, value]) => (
              <ValidatedForm.MultiSelectField
                key={key}
                label={key}
                formFieldName={key}
                propertyName="packFilters"
                placeholder="Select up to 3"
                maxSelections={3}
                options={value.map((item) => ({ text: item.name, value: item.id }))}
                onBlur={onBlur}
                disabled={disabled}
                value={values.packFilters}
                {...validatedFieldProps}
              />
            ))}
            {!this.isPaidPackEnabled() && (
              <TrialPeriodSettings
                values={values}
                trialOptions={packTrialPeriods}
                onBlur={onBlur}
                onChanged={save}
                disabled={disabled}
                validatedFieldProps={validatedFieldProps}
              />
            )}
          </>
        )}
      </div>
    );
  };

  getValidateFieldProps = (): any => {
    const {
      errors,
      touched,
      isValid,
      isValidating,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldTouched,
      dirty,
      resetForm,
    } = this.props;

    return {
      errors,
      touched,
      isFormValid: isValid && !isValidating,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldTouched,
      dirty,
      resetForm,
    };
  };
}

const LdWrapped = withLDConsumer()(PackForm);
export default LdWrapped;
